/*

* moraleja 1:

  usando `Array(height).fill(() => ... `
  hacia que se compartiese memoria. Lo correcto es hacer:
  usando `Array(height).fill().map(() => ...`

* moraleja 2 (falso, pero util):

  al tener un estado de tipo matriz `const [m, setM] = useState(matrix)`
  al renderizar como:
  ```
    m.map(_, rix)
      m[rix].map(_, cix)
        {m[rix][cix]}
  ```
  no re-renderiza cuando se actualiza la matriz.
  Para que react sea capaz de dectectar los cambios en la matriz y re-renderizar
  hay que usar el primer parametro del map:
  ```
    m.map(row, rix)
      row.map(cell, cix)
        {cell}
  ```

* moraleja 3:
  hay que hacer un deep copy de toda la matriz-2d al actualizarla para forzar
  el re-render.

*/

import { useContext } from "react"
import { UserContext } from "../../../store/store"

import { useMemo, useState } from "react"
import { make_radar, shipMargin } from "./utils"
import { Bomber } from "./Bomber"
import { Cell } from "./Cell"

// audios
import tada from "../../../assets/audio/tada.mp3"

const intersecta = (ship, pos) => {
  if (ship.dir === "h") {
    const mismaAltura = ship.pos.y === pos.y,
      mismaX = ship.pos.x <= pos.x && pos.x < ship.pos.x + ship.len
    return mismaAltura && mismaX
  } else {
    const mismaAbscisa = ship.pos.x === pos.x,
      mismaY = ship.pos.y <= pos.y && pos.y < ship.pos.y + ship.len
    return mismaAbscisa && mismaY
  }
}

const is_totally_sinked = (ship, radar) => {
  if (ship.dir === "h") {
    const r = radar[ship.pos.y]
      .slice(ship.pos.x, ship.pos.x + ship.len)
      .every((cell) => cell.status === "sinked")
    return r
  } else {
    const r = radar
      .slice(ship.pos.y, ship.pos.y + ship.len)
      .map((row) => row[ship.pos.x])
      .every((cell) => cell.status === "sinked")
    return r
  }
}

export const Grid = ({
  coords,
  confirmationCell,
  animations,
  setRatio,
  ships,
  isOpponent,
  winCallback,
  triggerBomb,
  onClick,
  setHoverCell,
}) => {
  const [state] = useContext(UserContext)

  const css_class =
    "crosswords ocean unselectable" +
    (animations ? " animated" : "") +
    " " +
    state.theme

  const [radar, setRadar] = useState(
    make_radar(coords.xcoords.length, coords.ycoords.length, ships)
  )

  const total = useMemo(
    () => ships.reduce((acc, ship) => acc + ship.len, 0),
    [ships]
  )

  const [pos, setBombaringPos] = useState(null)
  const doneBombing = (pos) => setBombaringPos(null)

  // los barcos hundidos de op
  const [sinkeds, setSinkeds] = useState([])

  const dunked = (pos, ships, radar) => {
    const sinked = radar[pos.y][pos.x].status === "sinked"
    if (sinked) {
      // los tiro a todos?
      const ship = ships.find((ship) => intersecta(ship, pos)),
        totally = is_totally_sinked(ship, radar)

      return ship && totally ? ship : false
    }
  }

  const doneNuking = (pos) => {
    // marco esa casilla como sinked o disabled segun corresponda
    setRadar((c) => {
      const es_hit = c[pos.y][pos.x].containes_ship
      // clono
      let n = c.map((inner) => inner.slice())
      // seteo
      n[pos.y][pos.x].status = es_hit ? "sinked" : "disabled"
      return n
    })

    // ha sido un hit?
    if (isOpponent) {
      const dunked_ship = dunked(pos, ships, radar)
      if (dunked_ship) {
        setSinkeds((currents) => currents.concat(dunked_ship))
        new Audio(tada).play()
      }
    }

    // calculo el nuevo porcentaje:
    const p = radar
      .flat()
      .reduce((acc, c) => acc + (c.status === "sinked" ? 1 : 0), 0)

    setRatio(1 - p / total)
    if (p === total) winCallback()
  }

  const handleClick = (x, y) => {
    const stillFlyin = pos !== null
    if (stillFlyin) return

    // setBombaringPos({x, y})
    onClick({ x, y })
  }

  // cargo los refs
  triggerBomb.current = (x, y) => {
    const stillFlyin = pos !== null
    if (stillFlyin) return

    setBombaringPos({ x, y })
  }

  return (
    <>
      {pos && (
        <Bomber bombard={pos} done={doneBombing} doneNuking={doneNuking} />
      )}

      <div className={css_class}>
        {(isOpponent ? sinkeds : ships).map((s, six) => (
          <div
            key={six}
            className="ship"
            data-dir={s.dir}
            data-len={s.len}
            style={shipMargin(s)}
          />
        ))}

        {radar.map((row, rix) => (
          <div key={rix} className="cell-row">
            {row.map((cell, cix) => (
              <Cell
                key={cix}
                cell={cell}
                confirmationCell={
                  confirmationCell &&
                  JSON.stringify(confirmationCell) ===
                    JSON.stringify({ x: cix, y: rix })
                }
                x={cix}
                y={rix}
                onClick={() => handleClick(cix, rix)}
                setHoverCell={setHoverCell}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  )
}
